import { DateFormats, formatDate } from '@strata/helper-functions';
import { DateInputFormik } from '@strata/react-date-time';
import { FormContainer } from '@strata/react-form-container';
import { PhoneInputFormik, TextInputFormik } from '@strata/react-inputs';
import { Spinner } from '@strata/react-loading-animation';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';

import { postNewTestMember } from 'api/oopsAPI';
import { handleError } from 'services/Error';
import { useGetPayerListQuery } from 'store/api/referenceApi';

const ValidationSchema = Yup.object({
    payerId: Yup.string().required(validationStrings.requiredField.errorMessage),
    memberId: Yup.string(),
    firstName: Yup.string(),
    lastName: Yup.string(),
    dateOfBirth: DateInputFormik.validator(),
    carrier: Yup.string(),
    account: Yup.string(),
    benefitGroup: Yup.string(),
    phone: PhoneInputFormik.validator(),
    email: Yup.string().email(),
    memberAddress1: Yup.string(),
    memberAddress2: Yup.string(),
    memberCity: Yup.string(),
    memberState: Yup.string(),
    memberZip: Yup.string(),
});

export const CreateMember = () => {
    const [generatedMember, setGeneratedMember] = useState<object>();

    const { data: payerList, isFetching: payerListFetching } = useGetPayerListQuery();

    return (
        <Formik
            initialValues={{} as Yup.InferType<typeof ValidationSchema>}
            validationSchema={ValidationSchema}
            onSubmit={async (values) => {
                try {
                    const response = await postNewTestMember({
                        payer_id: values.payerId,
                        member_id: values.memberId,
                        benefit_group: values.benefitGroup,
                        first_name: values.firstName,
                        last_name: values.lastName,
                        date_of_birth: formatDate(values.dateOfBirth, DateFormats.HYPHEN_DATE),
                        phone: values.phone,
                        email: values.email,
                        member_address1: values.memberAddress1,
                        member_address2: values.memberAddress2,
                        member_city: values.memberCity,
                        member_state: values.memberState,
                        member_zip: values.memberZip,
                        carrier: values.carrier,
                        account: values.account,
                    });
                    SuccessToast({ message: 'Test Member created.' });
                    setGeneratedMember(response);
                } catch (err) {
                    handleError('Error creating test member.', err as Error);
                }
            }}
        >
            {(formik) => (
                <FormContainer
                    formTitle="Create Test Member"
                    primaryButtonLabel="Create Member"
                    onFormSubmit={formik.handleSubmit}
                    isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                    onCancelClick={formik.resetForm}
                    formLeftContent={
                        <>
                            <SuggestSingleSelectFormik
                                label="Payer Id"
                                id="payerId"
                                required
                                isLoading={payerListFetching}
                                values={payerList}
                                selectedItem={payerList?.find(
                                    ({ payerId }) => payerId === formik.values.payerId,
                                )}
                                onChange={(newValue) => {
                                    void formik.setFieldValue('payerId', newValue?.payerId);
                                }}
                            />
                            <TextInputFormik
                                label="Member Id"
                                id="memberId"
                                helperText="Cannot exceed 12 characters for FMH+Pharmacies Automation Templates."
                            />
                            <TextInputFormik label="First Name" id="firstName" />
                            <TextInputFormik label="Last Name" id="lastName" />
                            <DateInputFormik id="dateOfBirth" label="Date Of Birth" />
                            <TextInputFormik id="carrier" label="Carrier" />
                            <TextInputFormik id="account" label="Account" />
                            <TextInputFormik label="Benefit Group" id="benefitGroup" />
                            <TextInputFormik id="relationshipCode" label="Relationship Code" />
                            <PhoneInputFormik id="phone" label="Phone" />
                            <TextInputFormik id="email" label="Email" />
                            <TextInputFormik id="memberAddress1" label="Address Line 1" />
                            <TextInputFormik id="memberAddress2" label="Address Line 2" />
                            <TextInputFormik id="memberCity" label="City" />
                            <TextInputFormik id="memberState" label="State" />
                            <TextInputFormik id="memberZip" label="Zip" />
                        </>
                    }
                    formRightContent={
                        <>
                            <div>Generated Member:</div>
                            {formik.isSubmitting ? (
                                <Spinner intent={Spinner.Intent.NONE} />
                            ) : (
                                <pre>
                                    {generatedMember && JSON.stringify(generatedMember, null, 2)}
                                </pre>
                            )}
                        </>
                    }
                />
            )}
        </Formik>
    );
};
